import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Grid,
  IconButton,
  Skeleton,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from '@mui/material';
import { Close } from '@mui/icons-material';
import StyledPlayDrawer from 'common/theme/play/StyledPlayDrawer';
import PlayExerciseCard from 'components/play/PlayExerciseCard';
import { PLAY_EXERCISES } from 'api/graphql/play';
import { LOCATIONS, locationLabel, muscleLabel } from 'components/play/constants';

/**
 * OTRIX Play — ejercicios de un músculo, en un panel lateral.
 *
 * Vive en un drawer a propósito: antes esta parrilla reemplazaba los tiles del home y se perdía
 * de vista la rutina mientras se exploraba.
 */
function PlayExerciseDrawer({ muscle = null, open, onClose, onAdd, onOpenDetail }) {
  const { t } = useTranslation(['play']);
  const [location, setLocation] = useState('');

  const { data, loading } = useQuery(PLAY_EXERCISES, {
    variables: { muscle_group: muscle || undefined, location: location || undefined },
    skip: !muscle,
    fetchPolicy: 'cache-and-network'
  });

  const exercises = data?.playExercises || [];

  const renderBody = () => {
    if (loading && exercises.length === 0) {
      return (
        <Grid container spacing={2.5}>
          {[0, 1, 2, 3].map((i) => (
            <Grid item xs={12} sm={6} key={i}>
              <Skeleton variant="rounded" height={260} sx={{ borderRadius: '16px' }} />
            </Grid>
          ))}
        </Grid>
      );
    }

    if (exercises.length === 0) {
      return (
        <Box
          sx={{
            py: 6,
            textAlign: 'center',
            border: '1px dashed',
            borderColor: 'hairline.main',
            borderRadius: 2,
            color: 'text.secondary'
          }}>
          <Typography variant="body1" fontWeight={600}>
            {t('browser.empty', { ns: 'play' })}
          </Typography>
          <Typography variant="body2">{t('browser.emptyHint', { ns: 'play' })}</Typography>
        </Box>
      );
    }

    return (
      <Grid container spacing={2.5}>
        {exercises.map((ex) => (
          <Grid item xs={12} sm={6} key={ex.id}>
            <PlayExerciseCard exercise={ex} onAdd={onAdd} onOpenDetail={onOpenDetail} />
          </Grid>
        ))}
      </Grid>
    );
  };

  return (
    <StyledPlayDrawer anchor="right" open={open} onClose={onClose}>
      <Stack sx={{ height: '100%' }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1.5}
          sx={{
            px: 2.5,
            py: 2,
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: 'surface.sidebar',
            borderBottom: '1px solid',
            borderColor: 'hairline.main'
          }}>
          <Typography variant="h6" fontWeight={800} letterSpacing="-0.02em" noWrap>
            {t('home.drawer.title', { ns: 'play', muscle: muscleLabel(t, muscle) })}
          </Typography>
          <IconButton
            size="small"
            onClick={onClose}
            aria-label={t('home.drawer.close', { ns: 'play' })}
            sx={{ flex: 'none', border: '1px solid', borderColor: 'hairline.main' }}>
            <Close fontSize="small" />
          </IconButton>
        </Stack>

        <Box sx={{ px: 2.5, pt: 2 }}>
          <ToggleButtonGroup
            exclusive
            size="small"
            color="secondary"
            value={location}
            onChange={(_, v) => setLocation(v || '')}
            aria-label={t('browser.location', { ns: 'play' })}>
            {LOCATIONS.map((l) => (
              <ToggleButton key={l.value} value={l.value}>
                {locationLabel(t, l.value)}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5 }}>{renderBody()}</Box>
      </Stack>
    </StyledPlayDrawer>
  );
}

PlayExerciseDrawer.propTypes = {
  muscle: PropTypes.string,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  onOpenDetail: PropTypes.func.isRequired
};

export default PlayExerciseDrawer;
